<template>
  <div class="ca-page-control">
    <button
      v-for="dot in visibleDots"
      :key="dot.index"
      type="button"
      class="ca-page-control__dot"
      :class="{
        'ca-page-control__dot--active': modelValue === dot.index,
        'ca-page-control__dot--small': dot.small,
      }"
      :aria-label="`Ir para slide ${dot.index + 1}`"
      :aria-current="modelValue === dot.index ? 'true' : undefined"
      @click="$emit('update:modelValue', dot.index)"
    />
  </div>
</template>

<script setup>
import { computed } from "vue";

defineOptions({
  name: "CaPageControl",
});

const props = defineProps({
  count: {
    type: Number,
    required: true,
  },
  modelValue: {
    type: Number,
    default: 0,
  },
});

defineEmits(["update:modelValue"]);

const MAX_DOTS = 5;

const visibleDots = computed(() => {
  if (props.count < MAX_DOTS) {
    return Array.from({ length: props.count }, (_, i) => ({
      index: i,
      small: false,
    }));
  }

  if (props.count === MAX_DOTS) {
    const lastIndex = props.count - 1;
    return Array.from({ length: props.count }, (_, i) => ({
      index: i,
      small:
        (i === lastIndex && props.modelValue !== lastIndex) ||
        (i === 0 && props.modelValue === lastIndex),
    }));
  }

  let start = props.modelValue - Math.floor(MAX_DOTS / 2);
  start = Math.max(0, Math.min(start, props.count - MAX_DOTS));

  return Array.from({ length: MAX_DOTS }, (_, i) => {
    const index = start + i;
    const isFirst = i === 0;
    const isLast = i === MAX_DOTS - 1;
    const hasMoreBefore = start > 0;
    return {
      index,
      small:
        ((isFirst && hasMoreBefore) || isLast) && props.modelValue !== index,
    };
  });
});
</script>

<style scoped lang="scss">
.ca-page-control {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--size-3);
}

.ca-page-control__dot {
  width: 8px;
  height: 8px;
  padding: 0;
  border: none;
  border-radius: 24px;

  @include ca-bp("md") {
    width: 12px;
    height: 12px;
  }
  background-color: var(
    --ca-color-neutrals-inverted-30,
    rgba(255, 255, 255, 0.3)
  );
  cursor: pointer;
  // Only animate size/colour; `all` would also fade the focus outline (black -> colour).
  transition:
    width 0.2s ease,
    height 0.2s ease,
    background-color 0.2s ease;

  &:focus-visible {
    outline: 1px solid var(--ca-color-feedback-info);
    outline-offset: 1px;
  }

  &--active {
    width: 24px;
    background-color: var(--ca-color-neutrals-inverted, white);

    @include ca-bp("md") {
      width: 40px;
    }
  }

  &--small {
    width: 6px;
    height: 6px;
  }
}
</style>
